"use client";

import { useState, useMemo } from "react";
import { Minus, Plus, Droplet, Zap } from "lucide-react";
import * as SliderPrimitive from "@radix-ui/react-slider";

/**
 * Savings calculator component for electric vehicles.
 * Allows users to input their annual mileage and fuel consumption,
 * then calculates estimated savings when switching to an electric vehicle.
 */
export default function SavingsCalculator() {
  const [annualMileage, setAnnualMileage] = useState(10000);
  const [fuelConsumption, setFuelConsumption] = useState(5.0);
  const [fuelPrice, setFuelPrice] = useState(1.83);
  const [electricityPrice, setElectricityPrice] = useState(0.37);

  // Calculate fuel costs
  const fuelCosts = useMemo(() => {
    const litersPerYear = (annualMileage / 100) * fuelConsumption;
    return litersPerYear * fuelPrice;
  }, [annualMileage, fuelConsumption, fuelPrice]);

  // Calculate electricity costs (assuming 14.8 kWh/100 km average)
  const electricityCosts = useMemo(() => {
    const kWhPerYear = (annualMileage / 100) * 14.8;
    return kWhPerYear * electricityPrice;
  }, [annualMileage, electricityPrice]);

  // Calculate savings
  const savings = useMemo(() => {
    return fuelCosts - electricityCosts;
  }, [fuelCosts, electricityCosts]);

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat("de-DE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  const formatMileage = (num: number) => {
    return new Intl.NumberFormat("de-DE").format(num);
  };

  const handleFuelPriceChange = (delta: number) => {
    setFuelPrice((prev) => Math.max(0.1, Math.min(5.0, prev + delta)));
  };

  const handleElectricityPriceChange = (delta: number) => {
    setElectricityPrice((prev) => Math.max(0.1, Math.min(2.0, prev + delta)));
  };

  return (
    <div className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-5">
      {/* Left Section: Current Usage */}
      <div className="bg-card rounded-lg border p-10 shadow-sm lg:col-span-2">
        <div className="space-y-12">
          <h2 className="text-2xl font-bold">Aktuelle Nutzung</h2>

          {/* Annual Mileage */}
          <div className="space-y-6 border-b pb-6">
            <label className="text-foreground text-sm font-medium">
              Jährliche Kilometerleistung
            </label>
            <div className="text-2xl font-bold">
              {formatMileage(annualMileage)} km
            </div>
            <SliderPrimitive.Root
              value={[annualMileage]}
              onValueChange={(value) => setAnnualMileage(value[0])}
              min={5000}
              max={75000}
              step={1000}
              className="relative flex w-full touch-none items-center select-none"
            >
              <SliderPrimitive.Track className="bg-muted relative h-2 w-full grow overflow-hidden rounded-full">
                <SliderPrimitive.Range className="bg-primary absolute h-full" />
              </SliderPrimitive.Track>
              <SliderPrimitive.Thumb className="border-primary bg-primary focus-visible:ring-ring block h-6 w-6 cursor-pointer rounded-full border-2 shadow-md transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50" />
            </SliderPrimitive.Root>
            <div className="text-muted-foreground flex justify-between text-sm">
              <span>5.000 km</span>
              <span>75.000 km</span>
            </div>
          </div>

          {/* Fuel Consumption */}
          <div className="space-y-6">
            <label className="text-foreground text-sm font-medium">
              Durchschnittlicher Kraftstoffverbrauch
            </label>
            <div className="text-2xl font-bold">
              {fuelConsumption.toFixed(1).replace(".", ",")} l/100 km
            </div>
            <SliderPrimitive.Root
              value={[fuelConsumption]}
              onValueChange={(value) => setFuelConsumption(value[0])}
              min={3.5}
              max={10}
              step={0.1}
              className="relative flex w-full touch-none items-center select-none"
            >
              <SliderPrimitive.Track className="bg-muted relative h-2 w-full grow overflow-hidden rounded-full">
                <SliderPrimitive.Range className="bg-primary absolute h-full" />
              </SliderPrimitive.Track>
              <SliderPrimitive.Thumb className="border-primary bg-primary focus-visible:ring-ring block h-6 w-6 cursor-pointer rounded-full border-2 shadow-md transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50" />
            </SliderPrimitive.Root>
            <div className="text-muted-foreground flex justify-between text-sm">
              <span>3,5 l/100 km</span>
              <span>10 l/100 km</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Section: Savings Estimate */}
      <div className="bg-card rounded-lg border p-10 shadow-sm lg:col-span-3">
        <div className="space-y-12">
          <h2 className="text-2xl font-bold">
            Schätzung der jährlichen Einsparung
          </h2>

          {/* Estimated Savings */}
          <div className="space-y-4 border-b pb-6">
            <label className="text-foreground text-sm font-normal">
              Geschätzte Einsparungen *
            </label>
            <div className="text-4xl font-bold">
              {formatNumber(savings)} EUR
            </div>
          </div>

          {/* Costs Grid */}
          <div className="grid grid-cols-1 gap-10 md:grid-cols-2">
            {/* Fuel Costs */}
            <div className="space-y-6">
              <label className="text-foreground block text-sm font-normal">
                Geschätzte Kraftstoffkosten
              </label>
              <div className="flex items-center gap-4">
                <div className="bg-primary flex h-8 w-8 shrink-0 items-center justify-center rounded-full">
                  <Droplet
                    className="text-primary-foreground h-4 w-4"
                    strokeWidth={2}
                  />
                </div>
                <div className="flex-1 space-y-4">
                  <div className="text-2xl font-bold">
                    {formatNumber(fuelCosts)} EUR
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => handleFuelPriceChange(-0.01)}
                      className="border-primary text-primary hover:bg-muted/90 focus-visible:ring-ring flex h-6 w-6 cursor-pointer items-center justify-center rounded-full border-2 transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <span className="text-sm font-normal">
                      {fuelPrice.toFixed(2).replace(".", ",")} EUR/l
                    </span>
                    <button
                      type="button"
                      onClick={() => handleFuelPriceChange(0.01)}
                      className="border-primary text-primary hover:bg-muted/90 focus-visible:ring-ring flex h-6 w-6 cursor-pointer items-center justify-center rounded-full border-2 transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Electricity Costs */}
            <div className="space-y-6">
              <label className="text-foreground block text-sm font-normal">
                Geschätzte Stromkosten
              </label>
              <div className="flex items-center gap-4">
                <div className="bg-primary flex h-8 w-8 shrink-0 items-center justify-center rounded-full">
                  <Zap
                    className="text-primary-foreground h-4 w-4"
                    strokeWidth={2}
                  />
                </div>
                <div className="flex-1 space-y-4">
                  <div className="text-2xl font-bold">
                    {formatNumber(electricityCosts)} EUR
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => handleElectricityPriceChange(-0.01)}
                      className="border-primary text-primary hover:bg-muted/90 focus-visible:ring-ring flex h-6 w-6 cursor-pointer items-center justify-center rounded-full border-2 transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <span className="text-sm font-normal">
                      {electricityPrice.toFixed(2).replace(".", ",")} EUR/kWh
                    </span>
                    <button
                      type="button"
                      onClick={() => handleElectricityPriceChange(0.01)}
                      className="border-primary text-primary hover:bg-muted/90 focus-visible:ring-ring flex h-6 w-6 cursor-pointer items-center justify-center rounded-full border-2 transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
